import { Mediator, app, payload } from "modular-openscriptjs";
import { api, STRINGS } from "../declarations";
import { routes } from "../backendRoutes";
import { BusinessVoiceOrderCallClient } from "../classes/BusinessVoiceOrderCallClient";
import { $e } from "../ojs.events";
import { eParse, inform } from "../helpers";

const broker = app("broker");

/**
 * Wires the framework-agnostic BusinessVoiceOrderCallClient (see
 * src/classes/BusinessVoiceOrderCallClient.js) into this app's event
 * system - the business-facing counterpart to app.carata's
 * VoiceOrderCall.js mediator, reusing the exact same $e.voiceOrderCall
 * event names so both apps' call widgets follow the same flow.
 *
 * Handles both backend mutations (triggering toasts and broker updates)
 * and frontend-only navigation tools (routing to orders, inventory, analytics).
 */
export class BusinessVoiceOrderCall extends Mediator {
	client = null;

	$$voiceOrderCall = {
		needs: {
			start: (ed, event) => this.startCall(ed),
			end: (ed, event) => this.endCall(ed),
			setSpeakerphoneOn: (ed, event) => {
				const { message } = eParse(ed);
				this.client?.setSpeakerphoneOn(!!message.get("enabled"));
			},
		},

		has: {
			statusChanged: (ed, event) => {},
			transcript: (ed, event) => {},
			toolCalled: (ed, event) => {},
			heartbeat: (ed, event) => {},
			audioLevel: (ed, event) => {},
			ended: (ed, event) => {
				broker.send($e.aiCredit.needs.balance);
			},
			error: (ed, event) => {},
		},
	};

	/**
	 * Start a new voice call, unless one is already in progress.
	 */
	async startCall() {
		if (this.client && !["idle", "ended"].includes(this.client.status)) {
			return;
		}

		this.client = new BusinessVoiceOrderCallClient({
			requester: api,
			sessionsPath: routes.voiceOrderSessions,
			companyId: window.currentCompany?.id,
		});

		this.client.addEventListener("status", (e) =>
			this.broadcast($e.voiceOrderCall.has.statusChanged, payload(e.detail)),
		);
		this.client.addEventListener("transcript", (e) =>
			this.broadcast($e.voiceOrderCall.has.transcript, payload(e.detail)),
		);
		this.client.addEventListener("toolcall", (e) => {
			this.broadcast($e.voiceOrderCall.has.toolCalled, payload(e.detail));
			this.performFrontendToolAction(e.detail);
		});
		this.client.addEventListener("heartbeat", (e) =>
			this.broadcast($e.voiceOrderCall.has.heartbeat, payload(e.detail)),
		);
		this.client.addEventListener("audiolevel", (e) =>
			this.broadcast($e.voiceOrderCall.has.audioLevel, payload(e.detail)),
		);
		this.client.addEventListener("error", (e) => {
			// The underlying exception (network failure, CapacitorHttp
			// error, getUserMedia rejection, etc.) is attached as `cause`
			// - log it here so it's visible in the device console (e.g.
			// via `adb logcat` / Chrome remote debugging on Android)
			// instead of just the generic user-facing message with no
			// further clue, mirroring app.carata's VoiceOrderCall.js.
			console.error(
				"[BusinessVoiceOrderCall] Voice call failed to start/continue:",
				e.detail.message,
				e.detail.cause,
			);
			this.broadcast($e.voiceOrderCall.has.error, payload(e.detail));
		});
		this.client.addEventListener("speakerphonechanged", (e) =>
			this.broadcast($e.voiceOrderCall.has.speakerphoneChanged, payload(e.detail)),
		);

		this.client.addEventListener("ended", (e) => {
			this.broadcast($e.voiceOrderCall.has.ended, payload(e.detail));
			this.client = null;
		});

		await this.client.start();
	}

	/**
	 * End the in-progress call, if any.
	 */
	async endCall() {
		await this.client?.stop("client_ended");
	}

	/**
	 * Performs client-side navigation and handles UI side-effects
	 * when Gemini Live invokes tools during the voice call.
	 */
	performFrontendToolAction({ name, args, result }) {
		const router = app("router");
		const companyId = window.currentCompany?.id;

		switch (name) {
			case "NavigateToDashboard":
				router.to("home");
				break;
			case "NavigateToAiCredits":
				router.to("user.ai-credits");
				break;
			case "NavigateToOrders":
				if (companyId) {
					router.to("business.orders", { companyId });
				} else {
					router.to("home");
				}
				break;
			case "NavigateToInventory":
				router.to("app.search");
				break;
			case "NavigateToManageInventory":
				if (companyId) {
					router.to("business.manage.inventory", { companyId });
				} else {
					router.to("home");
				}
				break;
			case "NavigateToAnalytics":
				router.to("business.analytics");
				break;
			case "NavigateToDeals":
				router.to("business.deals");
				break;
			case "NavigateToServices":
				if (companyId) {
					router.to("business.services.manage", { companyId });
				}
				break;
			case "NavigateToBranches":
				if (companyId) {
					router.to("business.manage.branches", { companyId });
				}
				break;
			case "NavigateToStaff":
				if (companyId) {
					router.to("business.manage.staff", { companyId });
				}
				break;
			case "NavigateToProfile":
				if (companyId) {
					router.to("business.manage.primary", { companyId });
				}
				break;
			case "OpenAddProductForm":
				if (companyId) {
					router.to("business.products.add", { companyId });
				}
				break;
			case "OpenPickupDropoffForm":
				// The delivery-request form lives inside the dashboard
				// (AuthenticatedIndex.js), not a persistent app-shell
				// component like app.carata's DeliveryRequestForm.js, so
				// navigate home first to make sure it's actually mounted
				// before asking it to open - mirrors the fetchBusinessOnlineStatus
				// resync delay used elsewhere in this file for the same
				// "let the UI settle after triggering something" reason.
				router.to("home");
				setTimeout(() => {
					broker.send($e.deliveryRequest.needs.requestForm);
				}, 400);
				break;
			case "ClosePickupDropoffForm":
				broker.send($e.deliveryRequest.needs.closeRequestForm);
				break;
			case "NavigateToDealReports":
				if (companyId) {
					router.to("business.deals.reports", { companyId });
				}
				break;
			case "NavigateToViewServices":
				if (companyId) {
					router.to("business.services.view", { companyId });
				}
				break;
			case "NavigateToUserProfile":
				router.to("user.view-profile");
				break;
			case "NavigateToManageBusiness":
				router.to("business.manage");
				break;
			case "NavigateToSettings":
				router.to("user.settings");
				break;
			case "NavigateToBusinessList":
				router.to("all-businesses");
				break;
			case "NavigateToAvailability":
				if (companyId) {
					router.to("business.manage.availability", { companyId });
				}
				break;
			case "NavigateToDeliveryRequests":
				if (companyId) {
					router.to("business.delivery-requests", { companyId });
				}
				break;
			case "NavigateToReviews":
				if (companyId) {
					router.to("business.reviews", { companyId });
				}
				break;
			case "NavigateToNotifications":
				if (companyId) {
					router.to("business.notifications", { companyId });
				}
				break;
			case "NavigateToSupportInfo":
				if (companyId) {
					router.to("business.manage.support", { companyId });
				}
				break;
			case "NavigateToLegalInfo":
				if (companyId) {
					router.to("business.manage.legal", { companyId });
				}
				break;

			// Reactive side-effects for mutations
			case "AddProduct": {
				inform("Product added to catalog!", STRINGS.SUCCESS);
				let productId = null;
				if (result) {
					if (typeof result === "object" && result !== null) {
						productId = result?.data?.product_id || result?.product_id;
					} else if (typeof result === "string") {
						try {
							const parsed = JSON.parse(result);
							productId = parsed?.data?.product_id || parsed?.product_id;
						} catch (_) {
							const match = result.match(/product_id:\s*(\d+)/i);
							if (match) {
								productId = parseInt(match[1], 10);
							}
						}
					}
				}
				if (companyId && productId) {
					router.to("business.products.edit", { companyId, productId });
				}
				break;
			}
			case "EditProductBasicInfo":
				inform("Product updated!", STRINGS.SUCCESS);
				break;
			case "UpdateProductStock":
				inform("Product stock updated!", STRINGS.SUCCESS);
				break;
			case "SetProductAvailability":
				inform("Product availability updated!", STRINGS.SUCCESS);
				break;
			case "SetProductAttributes":
				inform("Product attributes updated!", STRINGS.SUCCESS);
				break;
			case "SetProductExtras":
				inform("Product extras updated!", STRINGS.SUCCESS);
				break;
			case "MarkCompanyOrderProcessing":
				inform("Order marked as preparing!", STRINGS.SUCCESS);
				this.broadcast($e.order.has.updatedStatus, payload({ orderId: args?.order_id }));
				break;
			case "MarkCompanyOrderReady":
				inform("Order marked as ready!", STRINGS.SUCCESS);
				this.broadcast($e.order.has.updatedStatus, payload({ orderId: args?.order_id }));
				break;
			default:
				break;
		}
	}
}
